import { useMemo } from "react";

/**
 * Hook to convert the formats of the DB to DevExtreme formats
 * @param {Array} headers - List of headers with their valueFormat
 * @returns {Array} Configured columns with the appropriate DevExtreme formats
 */
export const useFormatConverter = (headers) => {
  return useMemo(() => {
    if (!headers || !Array.isArray(headers)) {
      return [];
    }

    return headers.map((header) => {
      const { valueFormat, fieldName, caption, ...otherProps } = header;

      // If no format, return the column as is
      if (!valueFormat || typeof valueFormat !== "string") {
        return {
          dataField: fieldName,
          caption: caption,
          ...otherProps,
        };
      }

      const formatStr = valueFormat.trim();

      // Detection of dates (contains "yyyy")
      if (formatStr.includes("yyyy")) {
        return {
          dataField: fieldName,
          caption: caption,
          dataType: "date",
          format: formatStr,
          ...otherProps,
        };
      }

      // Detection of percentages (contains "%")
      if (formatStr.includes("%")) {
        const precision = calculatePrecision(formatStr);
        return {
          dataField: fieldName,
          caption: caption,
          dataType: "number",
          format: {
            type: "percent",
            precision: precision,
          },
          ...otherProps,
        };
      }

      // Detection of currencies (contains "€" or "$")
      const currencyMatch = formatStr.match(/[€$]/);
      if (currencyMatch) {
        const currency = currencyMatch[0] === "€" ? "EUR" : "USD";
        return {
          dataField: fieldName,
          caption: caption,
          dataType: "number",
          format: {
            type: "currency",
            currency: currency,
            precision: 0,
            useCurrencyAccountingStyle: false,
          },
          ...otherProps,
        };
      }

      // Detection of numbers (contains "#")
      if (formatStr.includes("#")) {
        const precision = calculatePrecision(formatStr);
        return {
          dataField: fieldName,
          caption: caption,
          dataType: "number",
          format: {
            type: "fixedPoint",
            precision: precision,
          },
          ...otherProps,
        };
      }

      // Format not recognized, return as is
      return {
        dataField: fieldName,
        caption: caption,
        format: formatStr,
        ...otherProps,
      };
    });
  }, [headers]);
};

/**
 * Calculate the precision from a format string
 * Ex: "#.##" -> 2, "#.###" -> 3, "#.##0" -> 2, "#.###0" -> 3
 * @param {string} formatStr - The format string
 * @returns {number} The precision (number of decimal places)
 */
const calculatePrecision = (formatStr) => {
  const decimalMatch = formatStr.match(/\.([#0]+)/);
  if (decimalMatch) {
    const decimalPart = decimalMatch[1];
    const hashCount = (decimalPart.match(/#/g) || []).length;
    return hashCount > 0 ? hashCount : (decimalPart.match(/0/g) || []).length;
  }
  return 2;
};
